import React, { useEffect, useRef, useState } from 'react';
import { View, Button } from "../../../src/Components/Themed"
import { Animated, PanResponder, StyleSheet, useWindowDimensions, Platform, Pressable } from 'react-native';
import { Icons } from '../../../src/Components';
import { useColors } from '../../../src/constants/useColors';

type EventCarouselProps<T> = {
    data: T[],
    renderItem: (info: { item: T, index: number }) => React.ReactElement,
    autoAdvanceDelay?: number,
    pauseOnInteraction?: boolean,
    onPageChange?: (page: number) => void,
}

const EventCarousel = <T,>({
    data,
    renderItem,
    autoAdvanceDelay = 12000,
    pauseOnInteraction = true,
    onPageChange
}: EventCarouselProps<T>) => {
    const [currentPage, setCurrentPage] = useState(0);
    const [isPaused, setIsPaused] = useState(false);
    const [containerWidth, setContainerWidth] = useState(0);
    const translateX = useRef(new Animated.Value(0)).current;
    const autoAdvanceTimer = useRef<NodeJS.Timeout | null>(null);
    const inactivityTimer = useRef<NodeJS.Timeout | null>(null);
    const Colors = useColors();
    const isWeb = Platform.OS === 'web';

    // Calculate total pages
    const totalPages = data.length;

    const clearAutoAdvanceTimer = () => {
        if (autoAdvanceTimer.current) {
            clearTimeout(autoAdvanceTimer.current);
            autoAdvanceTimer.current = null;
        }
    };

    const clearInactivityTimer = () => {
        if (inactivityTimer.current) {
            clearTimeout(inactivityTimer.current);
            inactivityTimer.current = null;
        }
    };

    const goToPage = React.useCallback((page: number) => {
        const validPage = Math.max(0, Math.min(page, totalPages - 1));
        setCurrentPage(validPage);

        Animated.timing(translateX, {
            toValue: -validPage * containerWidth,
            duration: 300,
            useNativeDriver: true,
        }).start();

        if (onPageChange) {
            onPageChange(validPage);
        }
    }, [totalPages, containerWidth, onPageChange]);

    const handleUserInteraction = React.useCallback(() => {
        setIsPaused(true);
        clearAutoAdvanceTimer();
        clearInactivityTimer();
    }, []);

    const startInactivityTimer = React.useCallback(() => {
        clearInactivityTimer();
        inactivityTimer.current = setTimeout(() => {
            setIsPaused(false);
        }, 3000); // Resume after 3 seconds of inactivity
    }, []);

    // Pan responder for swipe gestures - use useMemo to recreate when dependencies change
    const panResponder = React.useMemo(
        () => PanResponder.create({
            onStartShouldSetPanResponder: () => true,
            onMoveShouldSetPanResponder: (_, gestureState) => {
                return Math.abs(gestureState.dx) > 10 || Math.abs(gestureState.dy) > 10;
            },
            onPanResponderGrant: () => {
                if (pauseOnInteraction) {
                    handleUserInteraction();
                }
            },
            onPanResponderMove: (_, gestureState) => {
                const newTranslateX = -currentPage * containerWidth + gestureState.dx;
                translateX.setValue(newTranslateX);
            },
            onPanResponderRelease: (_, gestureState) => {
                const swipeThreshold = containerWidth * 0.25;

                if (gestureState.dx > swipeThreshold && currentPage > 0) {
                    // Swipe right - go to previous page
                    goToPage(currentPage - 1);
                } else if (gestureState.dx < -swipeThreshold && currentPage < totalPages - 1) {
                    // Swipe left - go to next page
                    goToPage(currentPage + 1);
                } else {
                    // Return to current page
                    goToPage(currentPage);
                }

                if (pauseOnInteraction) {
                    startInactivityTimer();
                }
            },
            onPanResponderTerminationRequest: () => false,
        }),
        [containerWidth, currentPage, totalPages, pauseOnInteraction, goToPage, handleUserInteraction, startInactivityTimer]
    );

    // Auto-advance logic
    useEffect(() => {
        if (isPaused || totalPages <= 1) {
            clearAutoAdvanceTimer();
            return;
        }

        clearAutoAdvanceTimer();
        autoAdvanceTimer.current = setTimeout(() => {
            const nextPage = (currentPage + 1) % totalPages;
            goToPage(nextPage);
        }, autoAdvanceDelay);

        return () => {
            clearAutoAdvanceTimer();
        };
    }, [currentPage, isPaused, totalPages, autoAdvanceDelay]);

    // Reset to first page when data changes
    useEffect(() => {
        goToPage(0);
    }, [data.length]);

    // Cleanup timers on unmount
    useEffect(() => {
        return () => {
            clearAutoAdvanceTimer();
            clearInactivityTimer();
        };
    }, []);

    // Handle mouse enter/leave for web
    const handleMouseEnter = () => {
        if (pauseOnInteraction) {
            handleUserInteraction();
        }
    };

    const handleMouseLeave = () => {
        if (pauseOnInteraction) {
            startInactivityTimer();
        }
    };

    const handlePrevious = () => {
        if (currentPage > 0) {
            if (pauseOnInteraction) {
                handleUserInteraction();
            }
            goToPage(currentPage - 1);
            if (pauseOnInteraction) {
                startInactivityTimer();
            }
        }
    };

    const handleNext = () => {
        if (currentPage < totalPages - 1) {
            if (pauseOnInteraction) {
                handleUserInteraction();
            }
            goToPage(currentPage + 1);
            if (pauseOnInteraction) {
                startInactivityTimer();
            }
        }
    };

    return (
        <View transparent style={styles.container} onLayout={(ev) => {
            const { width } = ev.nativeEvent.layout;
            if (width > 0 && width !== containerWidth) {
                setContainerWidth(width);
            }
        }}>
            {containerWidth > 0 && totalPages > 0 ? (
                <View
                    transparent
                    style={styles.carouselContainer}
                    onMouseEnter={handleMouseEnter}
                    onMouseLeave={handleMouseLeave}
                >
                    <Animated.View
                        style={[
                            styles.carouselTrack,
                            {
                                transform: [{ translateX }],
                                width: containerWidth * totalPages,
                            },
                        ]}
                        {...panResponder.panHandlers}
                    >
                        {data.map((item, index) => (
                            <View
                                key={index}
                                transparent
                                style={[styles.carouselItem, { width: containerWidth }]}
                            >
                                {renderItem({ item, index })}
                            </View>
                        ))}
                    </Animated.View>
                    {/* Navigation Chevrons for Desktop */}
                    {isWeb && currentPage > 0 && (
                        <Pressable
                            onPress={handlePrevious}
                            style={[styles.chevronButton, styles.chevronLeft]}
                        >
                            <View transparent style={styles.chevronIconContainer}>
                                <Icons.ChevronIcon direction="left" size={10} color={Colors.text.h1} />
                            </View>
                        </Pressable>
                    )}
                    {isWeb && currentPage < totalPages - 1 && (
                        <Pressable
                            onPress={handleNext}
                            style={[styles.chevronButton, styles.chevronRight]}
                        >
                            <View transparent style={styles.chevronIconContainer}>
                                <Icons.ChevronIcon direction="right" size={10} color={Colors.text.h1} />
                            </View>
                        </Pressable>
                    )}
                </View>
            ) : null}
        </View>
    );
};

const styles = StyleSheet.create({
    container: {
        flex: 1,
        position: 'relative',
    },
    carouselContainer: {
        flex: 1,
        overflow: 'hidden',
    },
    carouselTrack: {
        flexDirection: 'row',
        height: '100%',
    },
    carouselItem: {
        height: '100%',
        alignSelf: 'stretch',
    },
    chevronButton: {
        position: 'absolute',
        top: '50%',
        transform: [{ translateY: -20 }],
        zIndex: 10,
        backgroundColor: 'rgba(255, 255, 255, 0.3)',
        borderRadius: 20,
        width: 40,
        height: 40,
        justifyContent: 'center',
        alignItems: 'center',
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.25,
        shadowRadius: 3.84,
        elevation: 5,
        cursor: 'pointer',
    },
    chevronLeft: {
        left: 16,
    },
    chevronRight: {
        right: 16,
    },
    chevronIconContainer: {
        justifyContent: 'center',
        alignItems: 'center',
    },
});

export default EventCarousel;
